/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useEffect, useMemo } from 'react';
import { 
  Bell, 
  Clock, 
  Smartphone, 
  Volume2, 
  Mail, 
  MessageSquare, 
  ShieldCheck, 
  Sliders, 
  CheckCircle2, 
  AlertTriangle, 
  AlertOctagon, 
  Save, 
  RotateCcw, 
  X, 
  Send, 
  Activity, 
  Calendar,
  Layers,
  Sparkles,
  Flame
} from 'lucide-react';
import { 
  DC3NotificationSettings, 
  DC3Record, 
  UserProfile, 
  CostCenter 
} from '../types.ts';
import { 
  getDefaultDC3NotificationSettings, 
  evaluateDC3Records, 
  buildDC3WhatsAppAlertUrl,
  CRITICAL_STPS_NORMS
} from '../utils/dc3NotificationHelper.ts';
import { NotificationService } from '../lib/notificationService.ts';
import { fcmService } from '../lib/fcmService.ts';

interface DC3NotificationConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentUser: UserProfile | null;
  onSaveSettings: (newSettings: DC3NotificationSettings) => Promise<void> | void;
  dc3Records?: DC3Record[];
  costCenters?: CostCenter[];
}

export const DC3NotificationConfigModal: React.FC<DC3NotificationConfigModalProps> = ({
  isOpen,
  onClose,
  currentUser,
  onSaveSettings,
  dc3Records = [],
  costCenters = []
}) => {
  const [activeTab, setActiveTab] = useState<'channels' | 'frequency' | 'leaddays' | 'filters' | 'test'>('channels');
  const [settings, setSettings] = useState<DC3NotificationSettings>(() => {
    return currentUser?.dc3NotificationSettings || getDefaultDC3NotificationSettings();
  });
  const [browserPermStatus, setBrowserPermStatus] = useState<NotificationPermission>('default');
  const [fcmToken, setFcmToken] = useState<string | null>(null);
  const [isTesting, setIsTesting] = useState(false);
  const [testFeedback, setTestFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (currentUser?.dc3NotificationSettings) {
      setSettings(currentUser.dc3NotificationSettings);
    } else {
      const cached = localStorage.getItem('snfsic_dc3_notif_config');
      if (cached) {
        try {
          setSettings(JSON.parse(cached));
        } catch (e) {
          setSettings(getDefaultDC3NotificationSettings());
        }
      } else {
        setSettings(getDefaultDC3NotificationSettings());
      }
    }
  }, [currentUser, isOpen]);

  useEffect(() => {
    if (typeof window !== 'undefined' && 'Notification' in window) {
      setBrowserPermStatus(Notification.permission);
    }
    const token = fcmService.getCurrentToken();
    setFcmToken(token);
  }, [isOpen]);

  const handleRequestPermission = async () => {
    const granted = await NotificationService.requestPermission();
    if (typeof window !== 'undefined' && 'Notification' in window) {
      setBrowserPermStatus(Notification.permission);
    }
    if (granted) {
      setTestFeedback({
        type: 'success',
        message: '¡Permiso de notificaciones push concedido correctamente en tu navegador!'
      });
    } else {
      setTestFeedback({
        type: 'error',
        message: 'Permiso denegado por el navegador. Habilítalo en los ajustes del sitio.'
      });
    }
  };

  const evaluation = useMemo(() => {
    return evaluateDC3Records(dc3Records, settings, costCenters);
  }, [dc3Records, settings, costCenters]);

  if (!isOpen) return null;

  const handleChannelToggle = (channelKey: keyof DC3NotificationSettings['channels']) => {
    setSettings(prev => ({
      ...prev,
      channels: {
        ...prev.channels,
        [channelKey]: !prev.channels[channelKey]
      }
    }));
  };

  const handleLeadDayToggle = (leadKey: keyof DC3NotificationSettings['leadDays']) => {
    setSettings(prev => ({
      ...prev,
      leadDays: {
        ...prev.leadDays,
        [leadKey]: !prev.leadDays[leadKey]
      }
    }));
  };

  const applyPresetSTPSRecommended = () => {
    setSettings(prev => ({
      ...prev,
      enabled: true,
      frequency: 'daily',
      dailyTime: '08:00',
      channels: {
        browserPush: true,
        fcmPush: true,
        audioChime: true,
        emailDigest: true,
        inAppBanner: true,
        whatsappAlerts: false
      },
      leadDays: {
        days60: true,
        days30: true,
        days15: true,
        days7: true,
        days0: true,
        expired: true
      },
      minComplianceThresholdPercent: 90
    }));
  };

  const applyPresetUrgentOnly = () => {
    setSettings(prev => ({
      ...prev,
      enabled: true,
      frequency: 'realtime',
      leadDays: {
        days60: false,
        days30: false,
        days15: false,
        days7: true,
        days0: true,
        expired: true
      },
      filterCriticalNormsOnly: true,
      minComplianceThresholdPercent: 95
    }));
  };

  const handleTriggerTestNotification = async () => {
    setIsTesting(true);
    setTestFeedback(null);
    try {
      if (settings.channels.audioChime) {
        NotificationService.playChime('critical');
      }

      if (settings.channels.browserPush) {
        NotificationService.sendNotification(
          '🔔 Prueba de Alerta DC-3 (STPS)',
          {
            body: '¡Excelente! Tus notificaciones push de vencimiento de formatos DC-3 están configuradas y funcionando con éxito.',
            tag: 'test-dc3-alert',
            requireInteraction: false
          },
          'critical'
        );
      }

      if (settings.channels.fcmPush) {
        await fcmService.sendCriticalStatusNotification({
          title: '📲 Prueba Push FCM - Vencimiento DC-3',
          body: 'Notificación de prueba enviada a través de Firebase Cloud Messaging.',
          category: 'URGENTE',
          url: '/#interno?sec=formatos_dc3'
        });
      }

      setTestFeedback({
        type: 'success',
        message: '¡Notificación de prueba disparada con éxito! Revisa la alerta en tu pantalla y audio.'
      });
    } catch (e: any) {
      setTestFeedback({
        type: 'error',
        message: `Error al disparar prueba: ${e.message || e}`
      });
    } finally {
      setIsTesting(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSaveSettings(settings);
      localStorage.setItem('snfsic_dc3_notif_config', JSON.stringify(settings));
      onClose();
    } catch (err: any) {
      setTestFeedback({
        type: 'error',
        message: `Error al guardar configuración: ${err.message || err}`
      });
    } finally {
      setIsSaving(false);
    }
  };

  const whatsAppUrl = buildDC3WhatsAppAlertUrl(evaluation, settings.phoneRecipient || currentUser?.phone);

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-3 sm:p-6 bg-slate-950/70 backdrop-blur-sm overflow-y-auto animate-in fade-in duration-200">
      <div 
        className="relative w-full max-w-4xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[92vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Top Header */}
        <div className="p-5 sm:p-6 border-b border-slate-200 dark:border-slate-800 bg-gradient-to-r from-slate-900 via-indigo-950 to-slate-900 text-white relative">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 transition-colors"
            title="Cerrar modal"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pr-8">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-amber-500/20 border border-amber-400/30 flex items-center justify-center text-amber-400 shadow-lg">
                <Bell className="w-6 h-6 animate-pulse" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-xl font-bold tracking-tight text-white">
                    Configuración de Notificaciones DC-3
                  </h2>
                  <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/30">
                    STPS • SIRCE
                  </span>
                </div>
                <p className="text-xs sm:text-sm text-slate-300">
                  Gestiona canales push, frecuencias y alertas de caducidad de constancias laborales
                </p>
              </div>
            </div>

            {/* Master Switch */}
            <div className="flex items-center gap-3 bg-white/10 dark:bg-slate-800/80 px-3.5 py-2 rounded-xl border border-white/10 self-start sm:self-auto">
              <span className="text-xs font-medium text-slate-200">
                {settings.enabled ? 'Notificaciones Activas' : 'Notificaciones Pausadas'}
              </span>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.enabled}
                  onChange={(e) => setSettings(prev => ({ ...prev, enabled: e.target.checked }))}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-slate-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-amber-500"></div>
              </label>
            </div>
          </div>

          {/* Quick Presets Bar */}
          <div className="mt-4 pt-3 border-t border-white/10 flex flex-wrap items-center gap-2 text-xs">
            <span className="text-slate-400 flex items-center gap-1 font-medium">
              <Sparkles className="w-3.5 h-3.5 text-amber-400" /> Presets Rápidos:
            </span>
            <button
              type="button"
              onClick={applyPresetSTPSRecommended}
              className="px-2.5 py-1 rounded-md bg-white/10 hover:bg-white/20 text-slate-200 hover:text-white transition-colors flex items-center gap-1.5"
            >
              <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" /> Estándar STPS (Recomendado)
            </button>
            <button
              type="button"
              onClick={applyPresetUrgentOnly}
              className="px-2.5 py-1 rounded-md bg-white/10 hover:bg-white/20 text-slate-200 hover:text-white transition-colors flex items-center gap-1.5"
            >
              <Flame className="w-3.5 h-3.5 text-rose-400" /> Solo Alto Riesgo & Críticas
            </button>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="flex border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50 px-4 sm:px-6 overflow-x-auto no-scrollbar">
          <button
            onClick={() => setActiveTab('channels')}
            className={`flex items-center gap-2 px-4 py-3 text-xs sm:text-sm font-semibold border-b-2 whitespace-nowrap transition-all ${
              activeTab === 'channels'
                ? 'border-indigo-600 dark:border-indigo-400 text-indigo-600 dark:text-indigo-400'
                : 'border-transparent text-slate-500 hover:text-slate-800 dark:hover:text-slate-200'
            }`}
          >
            <Smartphone className="w-4 h-4" />
            Canales de Notificación
          </button>
          <button
            onClick={() => setActiveTab('frequency')}
            className={`flex items-center gap-2 px-4 py-3 text-xs sm:text-sm font-semibold border-b-2 whitespace-nowrap transition-all ${
              activeTab === 'frequency'
                ? 'border-indigo-600 dark:border-indigo-400 text-indigo-600 dark:text-indigo-400'
                : 'border-transparent text-slate-500 hover:text-slate-800 dark:hover:text-slate-200'
            }`}
          >
            <Clock className="w-4 h-4" />
            Frecuencias y Horarios
          </button>
          <button
            onClick={() => setActiveTab('leaddays')}
            className={`flex items-center gap-2 px-4 py-3 text-xs sm:text-sm font-semibold border-b-2 whitespace-nowrap transition-all ${
              activeTab === 'leaddays'
                ? 'border-indigo-600 dark:border-indigo-400 text-indigo-600 dark:text-indigo-400'
                : 'border-transparent text-slate-500 hover:text-slate-800 dark:hover:text-slate-200'
            }`}
          >
            <Calendar className="w-4 h-4" />
            Ventanas de Anticipación
          </button>
          <button
            onClick={() => setActiveTab('filters')}
            className={`flex items-center gap-2 px-4 py-3 text-xs sm:text-sm font-semibold border-b-2 whitespace-nowrap transition-all ${
              activeTab === 'filters'
                ? 'border-indigo-600 dark:border-indigo-400 text-indigo-600 dark:text-indigo-400'
                : 'border-transparent text-slate-500 hover:text-slate-800 dark:hover:text-slate-200'
            }`}
          >
            <Sliders className="w-4 h-4" />
            Filtros y Alcance
          </button>
          <button
            onClick={() => setActiveTab('test')}
            className={`flex items-center gap-2 px-4 py-3 text-xs sm:text-sm font-semibold border-b-2 whitespace-nowrap transition-all ${
              activeTab === 'test'
                ? 'border-indigo-600 dark:border-indigo-400 text-indigo-600 dark:text-indigo-400'
                : 'border-transparent text-slate-500 hover:text-slate-800 dark:hover:text-slate-200'
            }`}
          >
            <Activity className="w-4 h-4" />
            Diagnóstico y Prueba en Vivo
            {evaluation.alertItems.length > 0 && (
              <span className="w-2 h-2 rounded-full bg-amber-500 animate-ping"></span>
            )}
          </button>
        </div>

        {/* Tab Content Body */}
        <div className="p-5 sm:p-6 overflow-y-auto flex-1 space-y-6">
          {/* Feedback Banner */}
          {testFeedback && (
            <div
              className={`p-3.5 rounded-xl border flex items-start justify-between gap-3 text-xs sm:text-sm animate-in fade-in duration-200 ${
                testFeedback.type === 'success'
                  ? 'bg-emerald-50 dark:bg-emerald-950/40 border-emerald-200 dark:border-emerald-800 text-emerald-800 dark:text-emerald-200'
                  : 'bg-rose-50 dark:bg-rose-950/40 border-rose-200 dark:border-rose-800 text-rose-800 dark:text-rose-200'
              }`}
            >
              <div className="flex items-center gap-2">
                {testFeedback.type === 'success' ? (
                  <CheckCircle2 className="w-5 h-5 text-emerald-600 dark:text-emerald-400 shrink-0" />
                ) : (
                  <AlertOctagon className="w-5 h-5 text-rose-600 dark:text-rose-400 shrink-0" />
                )}
                <span>{testFeedback.message}</span>
              </div>
              <button
                onClick={() => setTestFeedback(null)}
                className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* TAB 1: CHANNELS */}
          {activeTab === 'channels' && (
            <div className="space-y-5">
              <div>
                <h3 className="text-base font-bold text-slate-900 dark:text-white">
                  Canales de Entrega de Notificaciones
                </h3>
                <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">
                  Selecciona los medios a través de los cuales deseas recibir las alertas automáticas de caducidad.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* 1. Browser Native Push */}
                <div className={`p-4 rounded-xl border transition-all ${
                  settings.channels.browserPush 
                    ? 'border-indigo-300 dark:border-indigo-700 bg-indigo-50/40 dark:bg-indigo-950/20' 
                    : 'border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/40 opacity-75'
                }`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div className="p-2.5 rounded-lg bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 border border-indigo-500/20">
                        <Bell className="w-5 h-5" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-sm text-slate-900 dark:text-white flex items-center gap-2">
                          Notificaciones Push Web (Navegador)
                          {browserPermStatus === 'granted' ? (
                            <span className="text-[10px] bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 px-1.5 py-0.5 rounded-md font-bold">
                              Permitido
                            </span>
                          ) : (
                            <span className="text-[10px] bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 px-1.5 py-0.5 rounded-md font-bold">
                              {browserPermStatus === 'denied' ? 'Bloqueado' : 'Pendiente'}
                            </span>
                          )}
                        </h4>
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                          Alertas emergentes de escritorio y móvil a través del Service Worker.
                        </p>
                      </div>
                    </div>
                    <input
                      type="checkbox"
                      checked={settings.channels.browserPush}
                      onChange={() => handleChannelToggle('browserPush')}
                      className="w-5 h-5 text-indigo-600 rounded border-slate-300 focus:ring-indigo-500 cursor-pointer"
                    />
                  </div>

                  {browserPermStatus !== 'granted' && (
                    <div className="mt-3 pt-3 border-t border-slate-200 dark:border-slate-800 flex items-center justify-between">
                      <span className="text-xs text-amber-600 dark:text-amber-400">
                        Requiere permiso del navegador
                      </span>
                      <button
                        type="button"
                        onClick={handleRequestPermission}
                        className="px-2.5 py-1 text-xs font-semibold rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white transition-colors"
                      >
                        Habilitar Permiso
                      </button>
                    </div>
                  )}
                </div>

                {/* 2. Firebase Cloud Messaging (FCM) */}
                <div className={`p-4 rounded-xl border transition-all ${
                  settings.channels.fcmPush 
                    ? 'border-indigo-300 dark:border-indigo-700 bg-indigo-50/40 dark:bg-indigo-950/20' 
                    : 'border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/40 opacity-75'
                }`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div className="p-2.5 rounded-lg bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20">
                        <Smartphone className="w-5 h-5" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-sm text-slate-900 dark:text-white flex items-center gap-2">
                          Firebase Cloud Messaging (FCM)
                          <span className="text-[10px] bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 px-1.5 py-0.5 rounded-md font-bold">
                            Nube
                          </span>
                        </h4>
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                          Entrega garantizada de notificaciones push en segundo plano para múltiples dispositivos.
                        </p>
                      </div>
                    </div>
                    <input
                      type="checkbox"
                      checked={settings.channels.fcmPush}
                      onChange={() => handleChannelToggle('fcmPush')}
                      className="w-5 h-5 text-indigo-600 rounded border-slate-300 focus:ring-indigo-500 cursor-pointer"
                    />
                  </div>
                  <div className="mt-3 pt-3 border-t border-slate-200 dark:border-slate-800 text-[11px] text-slate-500 dark:text-slate-400 flex items-center justify-between">
                    <span>Estado del Token FCM:</span>
                    <span className="font-mono text-indigo-600 dark:text-indigo-400 font-semibold">
                      {fcmToken ? 'Sincronizado' : 'Auto-registro activo'}
                    </span>
                  </div>
                </div>

                {/* 3. Audio Chime (Synthesizer) */}
                <div className={`p-4 rounded-xl border transition-all ${
                  settings.channels.audioChime 
                    ? 'border-indigo-300 dark:border-indigo-700 bg-indigo-50/40 dark:bg-indigo-950/20' 
                    : 'border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/40 opacity-75'
                }`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div className="p-2.5 rounded-lg bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20">
                        <Volume2 className="w-5 h-5" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-sm text-slate-900 dark:text-white">
                          Alerta Sonora Acústica (Chime Sintetizado)
                        </h4>
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                          Tono acústico distintivo al detectar constancias en estado crítico o por renovar.
                        </p>
                      </div>
                    </div>
                    <input
                      type="checkbox"
                      checked={settings.channels.audioChime}
                      onChange={() => handleChannelToggle('audioChime')}
                      className="w-5 h-5 text-indigo-600 rounded border-slate-300 focus:ring-indigo-500 cursor-pointer"
                    />
                  </div>
                  <div className="mt-3 pt-3 border-t border-slate-200 dark:border-slate-800 flex items-center justify-end">
                    <button
                      type="button"
                      onClick={() => NotificationService.playChime('critical')}
                      className="text-xs font-semibold text-emerald-600 dark:text-emerald-400 hover:underline flex items-center gap-1"
                    >
                      <Volume2 className="w-3.5 h-3.5" /> Probar Tono Acústico
                    </button>
                  </div>
                </div>

                {/* 4. In-App Banners & Badges */}
                <div className={`p-4 rounded-xl border transition-all ${
                  settings.channels.inAppBanner 
                    ? 'border-indigo-300 dark:border-indigo-700 bg-indigo-50/40 dark:bg-indigo-950/20' 
                    : 'border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/40 opacity-75'
                }`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div className="p-2.5 rounded-lg bg-sky-500/10 text-sky-600 dark:text-sky-400 border border-sky-500/20">
                        <Layers className="w-5 h-5" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-sm text-slate-900 dark:text-white">
                          Banners In-App & Centro de Alertas
                        </h4>
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                          Contador flotante y cintillos de atención dentro del tablero principal.
                        </p>
                      </div>
                    </div>
                    <input
                      type="checkbox"
                      checked={settings.channels.inAppBanner}
                      onChange={() => handleChannelToggle('inAppBanner')}
                      className="w-5 h-5 text-indigo-600 rounded border-slate-300 focus:ring-indigo-500 cursor-pointer"
                    />
                  </div>
                </div>

                {/* 5. Email Digest */}
                <div className={`p-4 rounded-xl border transition-all md:col-span-2 ${
                  settings.channels.emailDigest 
                    ? 'border-indigo-300 dark:border-indigo-700 bg-indigo-50/40 dark:bg-indigo-950/20' 
                    : 'border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/40 opacity-75'
                }`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div className="p-2.5 rounded-lg bg-purple-500/10 text-purple-600 dark:text-purple-400 border border-purple-500/20">
                        <Mail className="w-5 h-5" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-sm text-slate-900 dark:text-white">
                          Resumen y Alertas por Correo Electrónico
                        </h4>
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                          Envío del reporte ejecutivo de cumplimiento normativo y lista nominal a tu cuenta de correo registrada.
                        </p>
                      </div>
                    </div>
                    <input
                      type="checkbox"
                      checked={settings.channels.emailDigest}
                      onChange={() => handleChannelToggle('emailDigest')}
                      className="w-5 h-5 text-indigo-600 rounded border-slate-300 focus:ring-indigo-500 cursor-pointer"
                    />
                  </div>

                  {settings.channels.emailDigest && (
                    <div className="mt-3 pt-3 border-t border-slate-200 dark:border-slate-800 space-y-2">
                      <label className="text-xs font-medium text-slate-700 dark:text-slate-300">
                        Correos adicionales para copia (HSEQ, Recursos Humanos, Comisión de Capacitación):
                      </label>
                      <input
                        type="text"
                        value={settings.emailRecipients || ''}
                        onChange={(e) => setSettings(prev => ({ ...prev, emailRecipients: e.target.value }))}
                        placeholder="hseq@empresa.com, rh@empresa.com (separados por coma)"
                        className="w-full text-xs sm:text-sm px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>
                  )}
                </div>

                {/* 6. WhatsApp Web Alert Dispatcher */}
                <div className={`p-4 rounded-xl border transition-all md:col-span-2 ${
                  settings.channels.whatsappAlerts 
                    ? 'border-indigo-300 dark:border-indigo-700 bg-indigo-50/40 dark:bg-indigo-950/20' 
                    : 'border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/40 opacity-75'
                }`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div className="p-2.5 rounded-lg bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20">
                        <MessageSquare className="w-5 h-5" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-sm text-slate-900 dark:text-white">
                          Despacho Rápido a WhatsApp
                        </h4>
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                          Genera un enlace pre-formateado con el resumen de constancias vencidas y por renovar para enviar a supervisores de planta.
                        </p>
                      </div>
                    </div>
                    <input
                      type="checkbox"
                      checked={settings.channels.whatsappAlerts}
                      onChange={() => handleChannelToggle('whatsappAlerts')}
                      className="w-5 h-5 text-indigo-600 rounded border-slate-300 focus:ring-indigo-500 cursor-pointer"
                    />
                  </div>

                  {settings.channels.whatsappAlerts && (
                    <div className="mt-3 pt-3 border-t border-slate-200 dark:border-slate-800 flex flex-col sm:flex-row items-center gap-3">
                      <div className="w-full sm:flex-1">
                        <input
                          type="text"
                          value={settings.phoneRecipient || ''}
                          onChange={(e) => setSettings(prev => ({ ...prev, phoneRecipient: e.target.value }))}
                          placeholder="+52 442 123 4567 (Número de WhatsApp)"
                          className="w-full text-xs sm:text-sm px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500"
                        />
                      </div>
                      <a
                        href={whatsAppUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-3.5 py-2 text-xs font-semibold rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white transition-colors flex items-center gap-1.5 shrink-0"
                      >
                        <Send className="w-3.5 h-3.5" /> Abrir en WhatsApp
                      </a>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* TAB 2: FREQUENCY */}
          {activeTab === 'frequency' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-base font-bold text-slate-900 dark:text-white">
                  Frecuencia de Notificación y Horarios
                </h3>
                <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">
                  Establece con qué periodicidad el sistema consolidará y te alertará sobre los vencimientos.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                {[
                  {
                    id: 'daily',
                    title: '📅 Diaria (Recomendado)',
                    desc: 'Notificación matutina con el estado de constancias del día.',
                    badge: 'Estándar'
                  },
                  {
                    id: 'realtime',
                    title: '⚡ Tiempo Real',
                    desc: 'Dispara de inmediato al entrar a la app y al detectar cambios.',
                    badge: 'Inmediato'
                  },
                  {
                    id: 'weekly',
                    title: '🗓️ Semanal',
                    desc: 'Resumen consolidado al inicio de la semana de trabajo.',
                    badge: 'Resumen'
                  },
                  {
                    id: 'biweekly',
                    title: '🌓 Quincenal',
                    desc: 'Dos veces por mes para corte de planeación.',
                    badge: 'Corte'
                  },
                  {
                    id: 'monthly',
                    title: '📆 Mensual',
                    desc: 'Reporte mensual para la Comisión Mixta STPS.',
                    badge: 'Auditoría'
                  }
                ].map(freq => (
                  <div
                    key={freq.id}
                    onClick={() => setSettings(prev => ({ ...prev, frequency: freq.id as any }))}
                    className={`p-4 rounded-xl border cursor-pointer transition-all ${
                      settings.frequency === freq.id
                        ? 'border-indigo-600 dark:border-indigo-400 bg-indigo-50/50 dark:bg-indigo-950/40 ring-2 ring-indigo-600/20'
                        : 'border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-800/60 hover:border-slate-300 dark:hover:border-slate-700'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-sm text-slate-900 dark:text-white">
                        {freq.title}
                      </span>
                      <span className="text-[10px] font-semibold px-2 py-0.5 rounded bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300">
                        {freq.badge}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-2 leading-relaxed">
                      {freq.desc}
                    </p>
                  </div>
                ))}
              </div>

              <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-800 space-y-4">
                <h4 className="font-semibold text-sm text-slate-900 dark:text-white flex items-center gap-2">
                  <Clock className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                  Horario de Envío Programado
                </h4>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-medium text-slate-700 dark:text-slate-300 block mb-1.5">
                      Hora preferida de despacho matutino:
                    </label>
                    <input
                      type="time"
                      value={settings.dailyTime || '08:00'}
                      onChange={(e) => setSettings(prev => ({ ...prev, dailyTime: e.target.value }))}
                      className="w-full text-sm px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500"
                    />
                    <p className="text-[11px] text-slate-400 mt-1">
                      Horario local de la planta o centro de operaciones.
                    </p>
                  </div>

                  {settings.frequency === 'weekly' && (
                    <div>
                      <label className="text-xs font-medium text-slate-700 dark:text-slate-300 block mb-1.5">
                        Día de la semana para resumen:
                      </label>
                      <select
                        value={settings.weeklyDay || 1}
                        onChange={(e) => setSettings(prev => ({ ...prev, weeklyDay: Number(e.target.value) }))}
                        className="w-full text-sm px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500"
                      >
                        <option value={1}>Lunes (Inicio de semana operativa)</option>
                        <option value={2}>Martes</option>
                        <option value={3}>Miércoles</option>
                        <option value={4}>Jueves</option>
                        <option value={5}>Viernes (Cierre semanal)</option>
                      </select>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* TAB 3: LEAD DAYS */}
          {activeTab === 'leaddays' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-base font-bold text-slate-900 dark:text-white">
                  Ventanas de Anticipación de Vencimiento (Antelación)
                </h3>
                <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">
                  Las constancias DC-3 tienen vigencia de 1 año. Configura en qué momentos exactos deseas recibir las alertas previas y posteriores.
                </p>
              </div>

              <div className="space-y-3">
                {/* 60 days */}
                <div className="p-4 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-800/60 flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-sky-500/10 text-sky-600 dark:text-sky-400 flex items-center justify-center font-bold text-sm">
                      60d
                    </div>
                    <div>
                      <h4 className="font-semibold text-sm text-slate-900 dark:text-white">
                        60 Días de Anticipación (2 Meses)
                      </h4>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        Preventivo temprano para coordinar agenda de capacitadores externos o internos.
                      </p>
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    checked={settings.leadDays.days60}
                    onChange={() => handleLeadDayToggle('days60')}
                    className="w-5 h-5 text-indigo-600 rounded border-slate-300 focus:ring-indigo-500 cursor-pointer"
                  />
                </div>

                {/* 30 days - Standard STPS */}
                <div className="p-4 rounded-xl border-2 border-amber-400/60 dark:border-amber-500/50 bg-amber-50/30 dark:bg-amber-950/20 flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-amber-500/20 text-amber-600 dark:text-amber-400 flex items-center justify-center font-bold text-sm">
                      30d
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h4 className="font-semibold text-sm text-slate-900 dark:text-white">
                          30 Días de Anticipación (1 Mes - Estándar STPS)
                        </h4>
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-amber-500/20 text-amber-700 dark:text-amber-300">
                          Requerido
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        Plazo obligatorio para programar cursos de re-certificación según los lineamientos STPS.
                      </p>
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    checked={settings.leadDays.days30}
                    onChange={() => handleLeadDayToggle('days30')}
                    className="w-5 h-5 text-amber-600 rounded border-slate-300 focus:ring-amber-500 cursor-pointer"
                  />
                </div>

                {/* 15 days */}
                <div className="p-4 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-800/60 flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-amber-500/10 text-amber-600 dark:text-amber-400 flex items-center justify-center font-bold text-sm">
                      15d
                    </div>
                    <div>
                      <h4 className="font-semibold text-sm text-slate-900 dark:text-white">
                        15 Días de Anticipación (Crítico)
                      </h4>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        Alerta de alta prioridad para evitar suspensión de actividades en áreas de riesgo.
                      </p>
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    checked={settings.leadDays.days15}
                    onChange={() => handleLeadDayToggle('days15')}
                    className="w-5 h-5 text-indigo-600 rounded border-slate-300 focus:ring-indigo-500 cursor-pointer"
                  />
                </div>

                {/* 7 days */}
                <div className="p-4 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-800/60 flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-rose-500/10 text-rose-600 dark:text-rose-400 flex items-center justify-center font-bold text-sm">
                      7d
                    </div>
                    <div>
                      <h4 className="font-semibold text-sm text-slate-900 dark:text-white">
                        7 Días de Anticipación (Urgente)
                      </h4>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        Última semana de vigencia legal para impartir la evaluación del curso.
                      </p>
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    checked={settings.leadDays.days7}
                    onChange={() => handleLeadDayToggle('days7')}
                    className="w-5 h-5 text-rose-600 rounded border-slate-300 focus:ring-rose-500 cursor-pointer"
                  />
                </div>

                {/* 0 days */}
                <div className="p-4 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-800/60 flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-rose-500/20 text-rose-700 dark:text-rose-300 flex items-center justify-center font-bold text-sm">
                      0d
                    </div>
                    <div>
                      <h4 className="font-semibold text-sm text-slate-900 dark:text-white">
                        Día del Vencimiento (Caducidad Oficial)
                      </h4>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        Aviso el día exacto en que expira el año de validez de la constancia.
                      </p>
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    checked={settings.leadDays.days0}
                    onChange={() => handleLeadDayToggle('days0')}
                    className="w-5 h-5 text-rose-600 rounded border-slate-300 focus:ring-rose-500 cursor-pointer"
                  />
                </div>

                {/* Expired */}
                <div className="p-4 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-800/60 flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-rose-500/10 text-rose-600 dark:text-rose-400">
                      <AlertOctagon className="w-5 h-5" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-sm text-slate-900 dark:text-white">
                        Constancias Vencidas (Post-Vencimiento)
                      </h4>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        Recordatorio continuo de constancias ya caducadas que requieren actualización inmediata.
                      </p>
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    checked={settings.leadDays.expired}
                    onChange={() => handleLeadDayToggle('expired')}
                    className="w-5 h-5 text-rose-600 rounded border-slate-300 focus:ring-rose-500 cursor-pointer"
                  />
                </div>
              </div>
            </div>
          )}

          {/* TAB 4: FILTERS & SCOPE */}
          {activeTab === 'filters' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-base font-bold text-slate-900 dark:text-white">
                  Filtros, Alcance y Normativas STPS
                </h3>
                <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">
                  Filtra por centros de costos o prioriza únicamente cursos correspondientes a normas oficiales críticas.
                </p>
              </div>

              {/* Cost Center Scope Mode */}
              <div className="space-y-3">
                <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block">
                  Alcance de Centros de Costos:
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {[
                    { id: 'assigned', title: 'Mis Centros Asignados', desc: 'Solo centros autorizados para mi usuario.' },
                    { id: 'all', title: 'Todos los Centros', desc: 'Supervisión global de toda la empresa.' },
                    { id: 'custom', title: 'Personalizado', desc: 'Selección específica manual.' }
                  ].map(mode => (
                    <div
                      key={mode.id}
                      onClick={() => setSettings(prev => ({ ...prev, targetCostCenterMode: mode.id as any }))}
                      className={`p-3.5 rounded-xl border cursor-pointer transition-all ${
                        settings.targetCostCenterMode === mode.id
                          ? 'border-indigo-600 dark:border-indigo-400 bg-indigo-50/50 dark:bg-indigo-950/40 ring-2 ring-indigo-600/20'
                          : 'border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-800/60'
                      }`}
                    >
                      <h5 className="font-semibold text-xs sm:text-sm text-slate-900 dark:text-white">
                        {mode.title}
                      </h5>
                      <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1">
                        {mode.desc}
                      </p>
                    </div>
                  ))}
                </div>

                {settings.targetCostCenterMode === 'custom' && (
                  <div className="p-3.5 rounded-xl bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-800 space-y-2 mt-2">
                    <span className="text-xs font-medium text-slate-700 dark:text-slate-300">
                      Selecciona los Centros de Costos a monitorear:
                    </span>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-40 overflow-y-auto pr-1">
                      {costCenters.map(cc => {
                        const isChecked = (settings.customCostCenterIds || []).includes(cc.id);
                        return (
                          <label
                            key={cc.id}
                            className="flex items-center gap-2 p-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-xs text-slate-800 dark:text-slate-200 cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => {
                                const current = settings.customCostCenterIds || [];
                                const updated = isChecked
                                  ? current.filter(id => id !== cc.id)
                                  : [...current, cc.id];
                                setSettings(prev => ({ ...prev, customCostCenterIds: updated }));
                              }}
                              className="w-4 h-4 text-indigo-600 rounded"
                            />
                            <span className="truncate font-medium">{cc.name}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              {/* Critical STPS Norms Filter */}
              <div className="p-4 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-800/60 space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h4 className="font-semibold text-sm text-slate-900 dark:text-white flex items-center gap-2">
                      <Flame className="w-4 h-4 text-rose-500" />
                      Filtrar Exclusivamente Normas de Alto Riesgo STPS
                    </h4>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                      Solo emitir notificaciones para constancias vinculadas a actividades críticas.
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    checked={settings.filterCriticalNormsOnly || false}
                    onChange={(e) => setSettings(prev => ({ ...prev, filterCriticalNormsOnly: e.target.checked }))}
                    className="w-5 h-5 text-indigo-600 rounded border-slate-300 focus:ring-indigo-500 cursor-pointer"
                  />
                </div>

                <div className="flex flex-wrap gap-1.5 pt-2 border-t border-slate-100 dark:border-slate-800 text-[11px]">
                  {CRITICAL_STPS_NORMS.map(norm => (
                    <span
                      key={norm}
                      className="px-2 py-0.5 rounded-md bg-slate-100 dark:bg-slate-800 font-mono font-medium text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700"
                    >
                      {norm}
                    </span>
                  ))}
                </div>
              </div>

              {/* Minimum Compliance Threshold Slider */}
              <div className="p-4 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-800/60 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-semibold text-sm text-slate-900 dark:text-white">
                      Umbral Mínimo de Cumplimiento Normativo
                    </h4>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      Disparar alerta si el porcentaje de constancias vigentes cae por debajo de esta meta.
                    </p>
                  </div>
                  <span className="text-base font-bold font-mono px-3 py-1 rounded-lg bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 border border-indigo-200 dark:border-indigo-800">
                    {settings.minComplianceThresholdPercent || 90}%
                  </span>
                </div>

                <input
                  type="range"
                  min="50"
                  max="100"
                  step="5"
                  value={settings.minComplianceThresholdPercent || 90}
                  onChange={(e) => setSettings(prev => ({ ...prev, minComplianceThresholdPercent: Number(e.target.value) }))}
                  className="w-full accent-indigo-600 cursor-pointer"
                />
                <div className="flex justify-between text-[10px] text-slate-400 font-mono">
                  <span>50% (Permisivo)</span>
                  <span>75% (Intermedio)</span>
                  <span>90% (Estándar STPS)</span>
                  <span>100% (Cero Tolerancia)</span>
                </div>
              </div>
            </div>
          )}

          {/* TAB 5: DIAGNOSTICS & LIVE TEST */}
          {activeTab === 'test' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-base font-bold text-slate-900 dark:text-white">
                  Diagnóstico y Simulación en Tiempo Real
                </h3>
                <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">
                  Comprueba el estado de los componentes de notificación y simula qué alertas se dispararían hoy.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="p-4 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-800/60">
                  <span className="text-xs text-slate-500 dark:text-slate-400">Permisos del Navegador</span>
                  <div className="mt-1 flex items-center gap-2 font-bold text-sm">
                    {browserPermStatus === 'granted' ? (
                      <span className="text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
                        <CheckCircle2 className="w-4 h-4" /> Concedido
                      </span>
                    ) : (
                      <span className="text-rose-600 dark:text-rose-400 flex items-center gap-1">
                        <AlertTriangle className="w-4 h-4" /> {browserPermStatus}
                      </span>
                    )}
                  </div>
                </div>

                <div className="p-4 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-800/60">
                  <span className="text-xs text-slate-500 dark:text-slate-400">FCM Cloud Token</span>
                  <div className="mt-1 font-mono text-xs font-bold text-indigo-600 dark:text-indigo-400 truncate">
                    {fcmToken ? `${fcmToken.slice(0, 16)}...` : 'Sincronizado'}
                  </div>
                </div>

                <div className="p-4 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-800/60">
                  <span className="text-xs text-slate-500 dark:text-slate-400">Índice de Cumplimiento</span>
                  <div className="mt-1 font-mono text-sm font-bold flex items-center gap-1.5">
                    <span className={evaluation.compliancePercent >= (settings.minComplianceThresholdPercent || 90) ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}>
                      {evaluation.compliancePercent}%
                    </span>
                    <span className="text-[11px] text-slate-400">({evaluation.totalAnalyzed} constancias)</span>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={handleTriggerTestNotification}
                  disabled={isTesting}
                  className="px-4 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-semibold text-xs sm:text-sm flex items-center gap-2 shadow-lg shadow-indigo-500/20 transition-all active:scale-95 disabled:opacity-50"
                >
                  <Bell className="w-4 h-4" />
                  {isTesting ? 'Disparando Alerta...' : '🔔 Disparar Notificación de Prueba en Vivo'}
                </button>

                <a
                  href={whatsAppUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-xs sm:text-sm flex items-center gap-2 shadow-lg shadow-emerald-500/20 transition-all active:scale-95"
                >
                  <MessageSquare className="w-4 h-4" />
                  💬 Compartir Resumen por WhatsApp
                </a>
              </div>

              {/* Simulation List */}
              <div className="p-4 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50 space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="font-semibold text-sm text-slate-900 dark:text-white flex items-center gap-2">
                    <ShieldCheck className="w-4 h-4 text-emerald-500" />
                    Simulación de Constancias que Dispararían Alerta Hoy ({evaluation.alertItems.length})
                  </h4>
                  <span className="text-xs text-slate-500">
                    {evaluation.expiringIn30DaysCount} por vencer • {evaluation.expiredCount} vencidas
                  </span>
                </div>

                {evaluation.alertItems.length === 0 ? (
                  <div className="p-6 text-center text-xs text-slate-500 dark:text-slate-400 bg-white dark:bg-slate-800 rounded-lg border border-dashed border-slate-200 dark:border-slate-700">
                    ✅ ¡Excelente! No hay constancias que coincidan con las reglas de alerta actuales. Todo el personal evaluado se encuentra al corriente.
                  </div>
                ) : (
                  <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
                    {evaluation.alertItems.map((item, idx) => (
                      <div
                        key={idx}
                        className="p-3 rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 flex items-center justify-between gap-3 text-xs"
                      >
                        <div className="flex items-center gap-2.5">
                          {item.severity === 'critical' ? (
                            <AlertOctagon className="w-4 h-4 text-rose-500 shrink-0" />
                          ) : (
                            <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />
                          )}
                          <div>
                            <div className="font-bold text-slate-900 dark:text-white">
                              {item.employeeName}
                            </div>
                            <div className="text-slate-500 dark:text-slate-400 text-[11px]">
                              {item.courseName}
                            </div>
                          </div>
                        </div>

                        <div className="text-right shrink-0">
                          <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold ${
                            item.severity === 'critical'
                              ? 'bg-rose-100 dark:bg-rose-900/40 text-rose-700 dark:text-rose-300'
                              : 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300'
                          }`}>
                            {item.badgeLabel}
                          </span>
                          <div className="text-[10px] text-slate-400 mt-0.5">
                            Caducidad: {item.expiryDate}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Modal Bottom Footer */}
        <div className="p-4 sm:p-5 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/80 flex flex-col sm:flex-row items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => setSettings(getDefaultDC3NotificationSettings())}
            className="text-xs font-semibold text-slate-500 hover:text-slate-800 dark:hover:text-slate-200 flex items-center gap-1.5 transition-colors self-start sm:self-auto"
          >
            <RotateCcw className="w-3.5 h-3.5" /> Restablecer Valores Predeterminados
          </button>

          <div className="flex items-center gap-3 w-full sm:w-auto justify-end">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-xs sm:text-sm font-semibold rounded-xl border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={isSaving}
              className="px-5 py-2 text-xs sm:text-sm font-semibold rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white flex items-center gap-2 shadow-lg shadow-indigo-500/20 transition-all active:scale-95 disabled:opacity-50"
            >
              <Save className="w-4 h-4" />
              {isSaving ? 'Guardando...' : 'Guardar Configuración'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
